import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import ImagePicker from '@/components/ImagePicker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { API_URL } from '@/config/api';

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: '考試須知',
    published: true,
    image_url: '',
    external_link: '',
    external_link_label: '',
  });

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/blogs?published_only=false`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBlog) {
        await axios.put(`${API_URL}/blogs/${editingBlog.id}`, formData);
        toast.success('更新成功');
      } else {
        await axios.post(`${API_URL}/blogs`, formData);
        toast.success('新增成功');
      }
      setDialogOpen(false);
      resetForm();
      fetchBlogs();
    } catch (error) {
      toast.error('操作失敗');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/blogs/${deleteTarget}`);
      toast.success('刪除成功');
      fetchBlogs();
    } catch (error) {
      toast.error('刪除失敗');
    } finally {
      setDeleteTarget(null);
    }
  };

  const openDialog = (blog = null) => {
    setPreviewHtml(null);
    if (blog) {
      setEditingBlog(blog);
      setFormData({
        title: blog.title,
        content: blog.content,
        excerpt: blog.excerpt,
        category: blog.category,
        published: blog.published,
        image_url: blog.image_url || '',
        external_link: blog.external_link || '',
        external_link_label: blog.external_link_label || '',
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingBlog(null);
    setPreviewHtml(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      category: '考試須知',
      published: true,
      image_url: '',
      external_link: '',
      external_link_label: '',
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div>
            <h1 className="text-3xl font-bold">考試資料管理</h1>
            <p className="text-muted-foreground">管理考試資料文章</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => openDialog()} data-testid="add-blog-button">
                <Plus className="mr-2 h-4 w-4" /> 新增文章
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingBlog ? '編輯文章' : '新增文章'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>標題 *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>分類</Label>
                    <Input
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>摘要</Label>
                  <Input
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    placeholder="文章摘要（顯示在列表頁）"
                  />
                </div>
                <ImagePicker
                  label="特色圖片"
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                />
                <div className="border rounded-lg p-4 space-y-3 bg-blue-50/50">
                  <p className="text-sm font-medium text-blue-700">官方連結（可選）</p>
                  <div>
                    <Label>連結說明文字</Label>
                    <Input
                      value={formData.external_link_label}
                      onChange={(e) => setFormData({ ...formData, external_link_label: e.target.value })}
                      placeholder="例如：運輸署網上預約系統"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>連結網址</Label>
                    <Input
                      value={formData.external_link}
                      onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                      placeholder="https://..."
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>內容（HTML）</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setPreviewHtml(previewHtml ? null : formData.content)}
                    >
                      {previewHtml ? <><EyeOff className="h-3 w-3 mr-1" /> 編輯</> : <><Eye className="h-3 w-3 mr-1" /> 預覽</>}
                    </Button>
                  </div>
                  {previewHtml ? (
                    <div
                      className="border rounded-lg p-4 min-h-[200px] prose prose-sm max-w-none bg-gray-50"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml, { ADD_ATTR: ['target'] }) }}
                    />
                  ) : (
                    <Textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={12}
                      className="font-mono text-sm"
                      placeholder="可使用 HTML 標籤格式化內容，例如：&#10;<h4>標題</h4>&#10;<p>段落文字</p>&#10;<ul><li>列表項目</li></ul>"
                    />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="published"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  />
                  <Label htmlFor="published">發布</Label>
                </div>
                <div className="flex gap-2">
                  <Button type="submit">儲存</Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    取消
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">載入中...</div>
        ) : blogs.length > 0 ? (
          <div className="bg-white rounded-lg border overflow-x-auto">
            <table className="w-full min-w-[480px]">
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="text-left p-4">標題</th>
                  <th className="text-left p-4 hidden md:table-cell">分類</th>
                  <th className="text-left p-4">狀態</th>
                  <th className="text-left p-4 hidden md:table-cell">建立時間</th>
                  <th className="text-right p-4">操作</th>
                </tr>
              </thead>
              <tbody>
                {blogs.map((blog) => (
                  <tr key={blog.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="p-4">
                      <div className="font-semibold">{blog.title}</div>
                      {blog.excerpt && (
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-1">{blog.excerpt}</div>
                      )}
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded">{blog.category}</span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${blog.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {blog.published ? '已發布' : '草稿'}
                      </span>
                    </td>
                    <td className="p-4 hidden md:table-cell text-sm text-muted-foreground">
                      {new Date(blog.created_at).toLocaleDateString('zh-HK')}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => openDialog(blog)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => setDeleteTarget(blog.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            暫無文章，請點擊「新增文章」開始撰寫。
          </div>
        )}
      </div>
      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除此文章？</AlertDialogTitle>
            <AlertDialogDescription>此操作不可撤銷，文章將永久刪除。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminBlogs;
